import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { RoleType } from "src/app/api/client";
import { UserInfoService } from "./user-info.service";

@Injectable({
  providedIn: "root",
})
export class AuthorizationService {
  protected readonly userInfo = inject(UserInfoService);

  /**
   * Checks if the user has all the specified roles.
   *
   * @param role - The primary role to check
   * @param otherRoles - Additional roles to check against the user's roles
   * @returns An Observable that emits true if the user has all specified roles, false otherwise
   *
   * @example
   * ```typescript
   * // Check if user has both 'ADMIN' and 'USER' roles
   * this.hasRoles('ADMIN', 'USER').subscribe(hasRoles => {
   *   if (hasRoles) {
   *     console.log('User has all required roles');
   *   }
   * });
   * ```
   */
  public hasRoles(role: RoleType, ...otherRoles: readonly RoleType[]): Observable<boolean> {
    return this.getUserRoles().pipe(
      map((userRoles) => {
        const userRoleSet: ReadonlySet<RoleType> = new Set(userRoles);
        return userRoleSet.has(role) && otherRoles.every((r) => userRoleSet.has(r));
      })
    );
  }

  /**
   * Checks if the user has at least one of the specified roles.
   *
   * @param role1 - First role to check
   * @param role2 - Second role to check
   * @param otherRoles - Additional roles to check against the user's roles
   * @returns An Observable that emits true if the user has any of the specified roles, false otherwise
   *
   * @example
   * ```typescript
   * // Check if user has either 'ADMIN' or 'USER' role
   * this.hasAnyRole('ADMIN', 'USER').subscribe(hasRole => {
   *   if (hasRole) {
   *     console.log('User has at least one of the required roles');
   *   }
   * });
   * ```
   */
  public hasAnyRole(role1: RoleType, role2: RoleType, ...otherRoles: readonly RoleType[]): Observable<boolean> {
    return this.getUserRoles().pipe(
      map((userRoles) => {
        const userRoleSet: ReadonlySet<RoleType> = new Set(userRoles);
        return userRoleSet.has(role1) || userRoleSet.has(role2) || otherRoles.some((r) => userRoleSet.has(r));
      })
    );
  }

  public getUserRoles(): Observable<readonly RoleType[]> {
    return this.userInfo.getMyUserInfo().pipe(map((x) => x?.roles?.map((y) => y.roleType) ?? []));
  }
}
